/**
 * User Settings V2 — "Authentication" section: password change/reset and,
 * for self-edit, the identity-provider link/unlink list.
 *
 * Contains:
 * - AuthenticationSection — the rendered SettingsSection (default export)
 * - IdentityProvidersSubsection — per-user IDP link/unlink list
 */

import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LockIcon from '@mui/icons-material/Lock';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { editUserIdp, fetchUserIdps } from '@sophocles/api/auth';
import { useUser } from '@sophocles/app';
import { addToast } from '@sophocles/features/toast';
import tokenService from '@sophocles/services/token';
import {
  ChangePasswordDialog,
  SettingsFieldRow,
  SettingsSection,
  SettingsSubsection,
} from '@sophocles/views/settings/components';

// Identity providers subsection — surfaces the IDPs the user's
// customer has enabled, lets them link/unlink each.  Hidden when
// the customer has none configured (the auth backend returns an
// empty list).  Linking redirects through Cognito to the IDP login
// flow; unlinking is in-place.  Logic ported from the legacy
// AuthenticationTab so the link/unlink UX matches what users had.
function IdentityProvidersSubsection({ userId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [idps, setIdps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState(null);

  const loadIdps = () => {
    setLoading(true);
    fetchUserIdps(userId)
      .then(data => setIdps(data ?? []))
      .catch(() => setIdps([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!userId) return;
    if (localStorage.getItem('odl.auth.linking') === 'true') {
      localStorage.removeItem('odl.auth.linking');
      dispatch(
        addToast({
          message: t('userSettings.successfullyLinkedAccount', 'Successfully linked your account.'),
          level: 'success',
        }),
      );
    }
    loadIdps();
  }, [userId]);

  const handleToggle = idp => {
    const newState = !idp.is_enabled;
    setPendingId(idp.id);
    editUserIdp(userId, idp.id, { state: newState ? 'on' : 'off' })
      .then(data => {
        if (newState && data?.url) {
          localStorage.setItem('odl.auth.linking', 'true');
          tokenService.logout();
          window.location.href = data.url;
          return;
        }
        loadIdps();
      })
      .catch(() => {
        dispatch(addToast({ message: t('errors.generic', 'Something went wrong.'), level: 'error' }));
      })
      .finally(() => setPendingId(null));
  };

  if (loading) {
    return (
      <SettingsSubsection title={t('userSettings.manageIdps', 'Manage your Identity Providers')}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      </SettingsSubsection>
    );
  }

  if (idps.length === 0) return null;

  return (
    <SettingsSubsection
      title={t('userSettings.manageIdps', 'Manage your Identity Providers')}
      description={t(
        'userSettings.manageIdpsDescription',
        'Link or unlink identity providers to enable single sign-on authentication.',
      )}
      bordered
    >
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
        {idps.map(idp => (
          <Stack key={idp.id} direction="row" alignItems="center" spacing={2} sx={{ py: 1.5 }}>
            <Box
              sx={{
                color: idp.is_enabled ? 'primary.main' : 'text.disabled',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {idp.is_enabled ? <LinkIcon /> : <LinkOffIcon />}
            </Box>
            <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {idp.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {idp.is_enabled ? t('userSettings.linked', 'Linked') : t('userSettings.notLinked', 'Not Linked')}
              </Typography>
            </Stack>
            <Button
              variant="soft"
              color={idp.is_enabled ? 'error' : 'primary'}
              startIcon={idp.is_enabled ? <LinkOffIcon /> : <LinkIcon />}
              onClick={() => handleToggle(idp)}
              disabled={pendingId === idp.id}
            >
              {idp.is_enabled ? t('userSettings.unlink', 'Unlink') : t('userSettings.link', 'Link')}
            </Button>
          </Stack>
        ))}
      </Stack>
    </SettingsSubsection>
  );
}

// Authentication section.
//
// Self-edit mode (no `targetUserId`):
//   - Password change for the auth user.
//   - IDP link/unlink subsection — hidden when the user's customer
//     has no IDPs configured (matches the legacy
//     UserSettingsView's `idpsLoaded && availableIdps.length > 0`
//     visibility gate).
//
// Admin-edit mode (`targetUserId` set, !== auth user):
//   - Password reset routed to the target user's id.  Same backend
//     endpoint (/users/<id>/set_password/), gated server-side by
//     UserPermission.  Helper text reflects the admin context.
//   - IDP subsection hidden — IDP linking inherently requires the
//     target user to be in an active session (Cognito federation
//     handshake), so admin can't usefully act on it.  Matches
//     legacy: EditUserPage doesn't expose AuthenticationTab at all.
export default function AuthenticationSection({ targetUserId }) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { user: authUser } = useUser();
  const authUserId = authUser?.get?.('id') ?? null;
  const isAdmin = targetUserId != null && targetUserId !== authUserId;
  const passwordUserId = isAdmin ? targetUserId : authUserId;
  return (
    <SettingsSection
      id="settings-authentication"
      icon={<LockIcon />}
      title={t('userSettings.v2.authentication', 'Authentication')}
      description={t('userSettings.v2.authenticationDescription', 'Manage how you sign in to the portal.')}
    >
      <Stack spacing={0}>
        <SettingsFieldRow
          label={t('userSettings.password', 'Password')}
          description={
            isAdmin
              ? t(
                  'userSettings.v2.adminPasswordDescription',
                  "Set a new password for this user. They'll be able to sign in with it immediately.",
                )
              : t('userSettings.v2.passwordDescription', 'Change the password used to sign in to the portal.')
          }
          divider={!!(!isAdmin && authUserId)}
        >
          <Button variant="outlined" onClick={() => setDialogOpen(true)} disabled={!passwordUserId}>
            {isAdmin
              ? t('userSettings.resetPassword', 'Reset Password')
              : t('userSettings.changePassword', 'Change Password')}
          </Button>
        </SettingsFieldRow>
        {!isAdmin && authUserId ? <IdentityProvidersSubsection userId={authUserId} /> : null}
      </Stack>
      <ChangePasswordDialog open={dialogOpen} onClose={() => setDialogOpen(false)} userId={passwordUserId} />
    </SettingsSection>
  );
}
